// Global imports
import slugify from 'slugify'
import { useEffect, useState, useRef, useCallback, useMemo } from 'react'
import {
  useFetcher,
  useSearchParams,
  useRouteLoaderData,
} from '@remix-run/react'
import { useDebugLogger } from '~/hooks'
import { mapBasketItem, getVariantPrices, getMatchingVariant } from '~/utils'

// Types
import type { BrendaChargesResult } from '~/types/brendaCharges'
import type {
  LunarProductPreBasket,
  LineItemProps,
  ProductDetailsVariantProps,
  RootDataProps,
  CustomerAddressProps,
  UserAddressProps,
} from '~/types'
import type { Splide as SplideInstance } from 'node_modules/@splidejs/react-splide/dist/types'

interface Props {
  variants: ProductDetailsVariantProps[]
  category?: string
}

interface AddToBasketProps {
  product?: LunarProductPreBasket | null
  extras?: LunarProductPreBasket[]
}

// Main export
const useProductSingle = ({ variants, category }: Props) => {
  // Debug logging — gated by the `DEBUG_CHECKOUT_FE` flag.
  const debugRef = useDebugLogger('DEBUG_CHECKOUT_FE')

  // Element Refs
  const thumbsRef = useRef<SplideInstance>(null)
  const mainRef = useRef<SplideInstance>(null)

  // Fetcher for basket actions (add items, flash errors)
  const basketFetcher = useFetcher()
  const basketLoading = basketFetcher.state !== 'idle'
  const { userAddress } = useRouteLoaderData('root') as RootDataProps

  // Submit items to the basket API route
  const addItems = useCallback(
    (items: LineItemProps[], userAddress?: UserAddressProps) => {
      basketFetcher.submit(JSON.stringify({ items, userAddress }), {
        method: 'post',
        encType: 'application/json',
        action: '/api/basket/items-add',
      })
    },
    [basketFetcher]
  )

  // Inline error state for add-to-basket failures
  const [basketError, setBasketError] = useState<string | null>(null)

  // Query search parameters
  // @NOTE: Using the "setSearchParams" of this causes re-renders when query strings are updated (causing multiple loader requests)
  // There doesn't currently seem to be any solid way of getting around this, therefore below we are just replacing the window history
  // for our query parameters, because all we really need them for is for the URL encase someone copies/revisits the page
  // @REF: https://github.com/remix-run/react-router/discussions/9851
  const [searchParams] = useSearchParams()

  // Address finder state
  const [address, setAddress] = useState<string>(userAddress?.line ?? '')
  const [addressData, setAddressData] = useState<CustomerAddressProps | null>(
    userAddress?.data ?? null
  )

  // Purchase related local state
  const [addressCheck, setAddressCheck] = useState<boolean>(false)
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false)
  const [purchaseModalOpen, setPurchaseModalOpen] = useState<boolean>(false)
  const [basketProduct, setBasketProduct] =
    useState<LunarProductPreBasket | null>(null)
  const [fulfilment, setFulfilment] = useState<string | null>(null)

  // VariantOptions sorts its values, so variants[0] isn't the one shown first
  const defaultVariant = slugify(
    [...variants]
      .map(e => e?.option?.value ?? '')
      .sort()[0]
      ?.toLowerCase() ?? ''
  )

  // Held in state as setQueryVariant writes to the URL via window.history,
  // which React Router doesn't observe
  const [selectedVariant, setSelectedVariant] = useState<string>(
    searchParams.get('variant') ?? defaultVariant
  )

  // Derived, not set in an effect, so the variant resolves on the server
  const activeVariant = useMemo(
    () => getMatchingVariant(variants, selectedVariant) ?? null,
    [variants, selectedVariant]
  )

  // Falls back to the cheapest variant when the URL names no variant
  const { fromPrice, salePrice } = useMemo(() => {
    if (!variants || variants.length === 0) {
      return { fromPrice: null, salePrice: null }
    }

    const lowest = variants.reduce((prev, curr) =>
      prev?.guidePrice?.amount < curr?.guidePrice?.amount ? prev : curr
    )

    const { variantGuidePrice, variantSalePrice } = getVariantPrices(
      activeVariant ?? lowest
    )

    return { fromPrice: variantGuidePrice, salePrice: variantSalePrice }
  }, [variants, activeVariant])

  // Seeds the default date range for hire products
  const averageHire = useMemo(() => {
    const average = activeVariant?.stringAttributes?.find(
      e => e.name == 'averageHireDuration'
    )

    return average?.value ? Number(average.value) : null
  }, [activeVariant])

  // Change active variant
  const setQueryVariant = (value: string) => {
    searchParams.set('variant', value)
    updateWindowHistory()
    setSelectedVariant(value)
  }

  // Updates the window history with the current searchParams
  const updateWindowHistory = () => {
    window.history.replaceState({}, '', '?' + searchParams.toString())
  }

  // Add items to the lunar basket
  const handleAddToBasket = useCallback(
    ({ product, extras }: AddToBasketProps) => {
      setBasketError(null)

      if (!product) {
        setBasketError('No product to add to basket')
        return
      }

      const basketItems = [
        { ...product, category },
        ...(extras ? extras : []),
      ].map(i => mapBasketItem(i))

      // User address to pass to basket
      const userAddressPayload =
        address && addressData
          ? { line: address, data: addressData }
          : undefined

      // Log the fully-mapped payload we're about to submit to the
      // basket API. Lets you see exactly what `hire_damage_waiver`,
      // `hire_deposit`, dates, etc. look like post-mapping — useful
      // when diagnosing mismatches between the charges response and
      // what Lunar ends up with on the line.
      debugRef.current('[useProductSingle] add to basket payload:', {
        items: basketItems,
        userAddress: userAddressPayload,
      })

      addItems(basketItems, userAddressPayload)
    },
    [address, addressData, addItems, category]
  )

  // Decide which flow to take the user down — opens the purchase
  // modal for products with extras, otherwise fires the direct
  // add-to-basket path.
  const chooseBasketFlow = useCallback(
    (product: LunarProductPreBasket, charges: BrendaChargesResult) => {
      const hasExtras =
        charges.product.addOns.length > 0 ||
        charges.product.crossSells.length > 0

      if (hasExtras) {
        setPurchaseModalOpen(true)
      } else {
        handleAddToBasket({ product })
      }
    },
    [setPurchaseModalOpen, handleAddToBasket]
  )

  // Resets the basket flow after user interaction
  const resetBasketFlow = () => {
    setBasketProduct(null)
    setAddressCheck(false)
  }

  // Sync the sliders
  const syncImageCarousels = (variant: ProductDetailsVariantProps) => {
    if (
      variant?.images &&
      mainRef.current &&
      thumbsRef.current &&
      thumbsRef.current.splide
    ) {
      mainRef.current.sync(thumbsRef.current.splide)
    }
  }

  // On variants/search params change reset our selected variant
  useEffect(() => {
    setSelectedVariant(searchParams.get('variant') ?? defaultVariant)
  }, [searchParams, variants, defaultVariant])

  // Splide side effects only, the variant itself is derived above
  useEffect(() => {
    if (activeVariant) {
      syncImageCarousels(activeVariant)
    }

    // Reset the carousel to the first slide on selection change
    if (selectedVariant && thumbsRef?.current?.splide) {
      thumbsRef.current?.go(0)
    }
  }, [activeVariant, selectedVariant])

  return {
    thumbsRef,
    mainRef,
    selectedVariant,
    activeVariant,
    fromPrice,
    salePrice,
    addressCheck,
    addressModalOpen,
    purchaseModalOpen,
    basketProduct,
    averageHire,
    address,
    addressData,
    basketLoading,
    basketError,
    fulfilment,
    setAddress,
    setAddressData,
    setAddressCheck,
    setAddressModalOpen,
    setPurchaseModalOpen,
    setBasketProduct,
    setQueryVariant,
    handleAddToBasket,
    chooseBasketFlow,
    resetBasketFlow,
    setBasketError,
    setFulfilment,
  }
}

export default useProductSingle
